/**
 * routeContext.ts — Shared helper for parsing the direct-route URL pattern
 * `/component/{module_seq}#tab={tab_seq}` used across the platform.
 *
 * Single source of truth so error-detection code (apiService.ts) and the
 * manual bug-report widget (PlatformHeader.tsx) never drift out of sync on
 * how module/tab sequence numbers are extracted from the URL.
 */

import { getActivePreview } from './componentPreviewContext';

export interface RouteContext {
    route: string;
    moduleSeq: number | null;
    tabSeq: number | null;
    /**
     * Set when `ComponentViewer` has a component mounted in its preview
     * dialog at capture time. `moduleSeq`/`tabSeq` above still describe the
     * real HOST route (e.g. Registry Manager) - this field identifies the
     * component actually being previewed, which is what any API failure
     * during preview is really about. See componentPreviewContext.ts.
     */
    previewSeq: number | null;
    previewFriendlyName: string | null;
}

export function captureRouteContext(): RouteContext {
    const pathMatch = window.location.pathname.match(/\/component\/(\d+)/);
    const hashMatch = window.location.hash.match(/tab=(\d+)/);
    const preview = getActivePreview();

    return {
        route: window.location.pathname + window.location.hash,
        moduleSeq: pathMatch ? parseInt(pathMatch[1], 10) : null,
        tabSeq: hashMatch ? parseInt(hashMatch[1], 10) : null,
        previewSeq: preview?.seq ?? null,
        previewFriendlyName: preview?.friendlyName ?? null,
    };
}
